import { useMemo, useState } from "react";
import { Box, MenuItem, TextField } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useRecoilValue } from "recoil";

import { formInputsStyles } from "../Configuration/styles";
import ExclusiveToggleGroup from "../components/Inputs/ExclusiveToggleGroup";
import ResponsiveDialog from "../components/ResponsiveDialog";
import { formatterService } from "../NumberFormatter";
import { FillRule, LevelDraft, RoundingDirection } from "./types";
import { levelName } from "./grid";

/**
 * What the generator is asked for. The three rounding fields tidy the
 * values it produces and are used once, here: what lands in the grid is
 * ordinary typed prices, and nothing recomputes them afterwards.
 */
export interface GenerateOptions {
  low: string;
  high: string;
  progressive: boolean;
  rounding_direction: RoundingDirection;
  rounding_step: string | null;
  rounding_ending: number | null;
}

interface Props {
  productName: string;
  levels: LevelDraft[];
  onApply: (rule: FillRule) => void;
  onGenerate: (options: GenerateOptions) => void;
  generating?: boolean;
  onClose: () => void;
}

type Mode = "step" | "range";

const ROUNDING_DIRECTIONS: RoundingDirection[] = ["nearest", "up", "down"];
// The multiples a grid actually lands on. A free number field invited "0.37".
const ROUNDING_STEPS = ["0.5", "1", "5", "10"];
const ROUNDING_LABELS: Record<RoundingDirection, string> = {
  nearest: "PRICING_TIER_ROUNDING_NEAREST",
  up: "PRICING_TIER_ROUNDING_UP",
  down: "PRICING_TIER_ROUNDING_DOWN",
};

/**
 * Describe a row instead of typing it.
 *
 * Real grids are built from rules: Corsica Linea's climbs by a flat +11 € up to
 * its twelfth level, then by about 6 %. Two rules replace seventeen prices.
 */
const FillRuleDialog = ({
  productName,
  levels,
  onApply,
  onGenerate,
  generating,
  onClose,
}: Props) => {
  const intl = useIntl();
  const { classes } = formInputsStyles();
  const [mode, setMode] = useState<Mode>("step");
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [progressive, setProgressive] = useState(true);
  const [start, setStart] = useState("");
  const [step, setStep] = useState("");
  const [unit, setUnit] = useState<FillRule["unit"]>("currency");
  const [fromIndex, setFromIndex] = useState(0);
  const [toIndex, setToIndex] = useState(levels.length - 1);
  const [direction, setDirection] = useState<RoundingDirection>("nearest");
  const [roundingStep, setRoundingStep] = useState("1");
  const [roundingEnding, setRoundingEnding] = useState<number | "">("");
  const formatter = useRecoilValue(formatterService);

  const rounding = useMemo(
    () => ({
      rounding_direction: direction,
      rounding_step: roundingStep.trim() === "" ? null : roundingStep.trim(),
      rounding_ending: roundingEnding === "" ? null : Number(roundingEnding),
    }),
    [direction, roundingStep, roundingEnding]
  );

  const startValue = Number(start);
  const stepValue = Number(step);
  const canApply =
    Number.isFinite(startValue) &&
    startValue > 0 &&
    Number.isFinite(stepValue) &&
    start.trim() !== "" &&
    step.trim() !== "" &&
    fromIndex <= toIndex;
  const canGenerate =
    Number(low) > 0 && Number(high) > Number(low) && levels.length >= 2;

  return (
    <ResponsiveDialog
      open
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      dialogName="pricing-tier-fill"
      title={intl.formatMessage(
        { id: "PRICING_TIER_FILL_TITLE" },
        { product: productName }
      )}
      onSubmit={() =>
        mode === "step"
          ? onApply({
              start: startValue,
              step: stepValue,
              unit,
              fromIndex,
              toIndex,
            })
          : onGenerate({ low, high, progressive, ...rounding })
      }
      submitDisabled={mode === "step" ? !canApply : !canGenerate}
      loading={generating}
      submitLabel={intl.formatMessage({ id: "PRICING_TIER_FILL_APPLY" })}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <ExclusiveToggleGroup
          value={mode}
          onChange={(next) => setMode(next as Mode)}
          options={[
            { value: "step", label: "PRICING_TIER_FILL_MODE_STEP" },
            { value: "range", label: "PRICING_TIER_FILL_MODE_RANGE" },
          ]}
        />

        {mode === "range" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              size="small"
              className={classes.inputText}
              type="number"
              label={intl.formatMessage({ id: "PRICING_TIER_FILL_LOW" })}
              value={low}
              onChange={(event) => setLow(event.target.value)}
              sx={{ flex: "1 1 140px" }}
            />
            <TextField
              size="small"
              className={classes.inputText}
              type="number"
              label={intl.formatMessage({ id: "PRICING_TIER_FILL_HIGH" })}
              value={high}
              onChange={(event) => setHigh(event.target.value)}
              sx={{ flex: "1 1 140px" }}
            />
            <TextField
              select
              size="small"
              className={classes.inputText}
              label={intl.formatMessage({ id: "PRICING_TIER_FILL_SPREAD" })}
              value={progressive ? "progressive" : "even"}
              onChange={(event) =>
                setProgressive(event.target.value === "progressive")
              }
              sx={{ flex: "1 1 170px" }}
            >
              <MenuItem value="progressive">
                <FormattedMessage id="PRICING_TIER_FILL_SPREAD_PROGRESSIVE" />
              </MenuItem>
              <MenuItem value="even">
                <FormattedMessage id="PRICING_TIER_FILL_SPREAD_EVEN" />
              </MenuItem>
            </TextField>

            {/* A geometric spread lands on 97.83; these two bring it back to a
            price anyone would dare display. */}
            <TextField
              select
              size="small"
              className={classes.inputText}
              label={intl.formatMessage({
                id: "PRICING_TIER_ROUNDING_DIRECTION",
              })}
              value={direction}
              onChange={(event) =>
                setDirection(event.target.value as RoundingDirection)
              }
              sx={{ flex: "1 1 150px" }}
            >
              {ROUNDING_DIRECTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  <FormattedMessage id={ROUNDING_LABELS[option]} />
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              className={classes.inputText}
              label={intl.formatMessage({ id: "PRICING_TIER_ROUNDING_STEP" })}
              value={roundingStep}
              onChange={(event) => setRoundingStep(event.target.value)}
              sx={{ flex: "1 1 130px" }}
            >
              <MenuItem value="">
                <FormattedMessage id="PRICING_TIER_ROUNDING_STEP_NONE" />
              </MenuItem>
              {ROUNDING_STEPS.map((option) => (
                <MenuItem key={option} value={option}>
                  <FormattedMessage
                    id="PRICING_TIER_ROUNDING_STEP_VALUE"
                    values={{ step: formatter.format(Number(option), {}) }}
                  />
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              className={classes.inputText}
              label={intl.formatMessage({ id: "PRICING_TIER_ROUNDING_ENDING" })}
              value={roundingEnding === "" ? "" : String(roundingEnding)}
              onChange={(event) =>
                setRoundingEnding(
                  event.target.value === "" ? "" : Number(event.target.value)
                )
              }
              sx={{ flex: "1 1 130px" }}
            >
              <MenuItem value="">
                <FormattedMessage id="PRICING_TIER_ROUNDING_ENDING_NONE" />
              </MenuItem>
              {[0, 5, 9].map((digit) => (
                <MenuItem key={digit} value={String(digit)}>
                  <FormattedMessage
                    id="PRICING_TIER_ROUNDING_ENDING_VALUE"
                    values={{ digit }}
                  />
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {mode === "step" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              size="small"
              className={classes.inputText}
              type="number"
              label={intl.formatMessage({ id: "PRICING_TIER_FILL_START" })}
              value={start}
              onChange={(event) => setStart(event.target.value)}
              sx={{ flex: "1 1 140px" }}
            />
            <TextField
              size="small"
              className={classes.inputText}
              type="number"
              label={intl.formatMessage({ id: "PRICING_TIER_FILL_STEP" })}
              value={step}
              onChange={(event) => setStep(event.target.value)}
              sx={{ flex: "1 1 140px" }}
            />
            <TextField
              select
              size="small"
              className={classes.inputText}
              label={intl.formatMessage({ id: "PRICING_TIER_FILL_UNIT" })}
              value={unit}
              onChange={(event) =>
                setUnit(event.target.value as FillRule["unit"])
              }
              sx={{ flex: "0 0 110px" }}
            >
              <MenuItem value="currency">€</MenuItem>
              <MenuItem value="percent">%</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              className={classes.inputText}
              label={intl.formatMessage({ id: "PRICING_TIER_FILL_FROM" })}
              value={fromIndex}
              onChange={(event) => setFromIndex(Number(event.target.value))}
              sx={{ flex: "1 1 160px" }}
            >
              {levels.map((level, index) => (
                <MenuItem key={level.key} value={index}>
                  {levelName(level, index)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              className={classes.inputText}
              label={intl.formatMessage({ id: "PRICING_TIER_FILL_TO" })}
              value={toIndex}
              onChange={(event) => setToIndex(Number(event.target.value))}
              sx={{ flex: "1 1 160px" }}
            >
              {levels.map((level, index) => (
                <MenuItem key={level.key} value={index}>
                  {levelName(level, index)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </Box>
    </ResponsiveDialog>
  );
};

export default FillRuleDialog;
